import {EnvelopeNewIcon, ChatRegularOptionsIcon} from 'shells/icon';
import {StatusBadge, type Level} from 'shells/status-badge/status-badge';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';

import type {
    EmailDeliverabilityStatusType as EmailStatus,
    SmsDeliverabilityStatusType as SmsStatus,
    SmsDeliverabilityFragment as SmsDeliverabilityStatus,
    EmailDeliverabilityFragment as EmailDeliverabilityStatus,
    UnsubscribeSource,
} from 'nutshell-graphql-types';

interface Props {
    deliverabilityStatus: EmailDeliverabilityStatus | SmsDeliverabilityStatus;
    iconOnly: boolean;
}

export function DeliverabilityStatusBadge(props: Props) {
    const {deliverabilityStatus, iconOnly} = props;

    if (deliverabilityStatus.status === 'DELIVERABLE') {
        return null;
    }

    const isEmailStatus = deliverabilityStatus.__typename === 'DeliverabilityStatus';
    const isSmsStatus = deliverabilityStatus.__typename === 'SmsDeliverabilityStatus';

    const unsubscribeStats = isEmailStatus ? deliverabilityStatus.unsubscribeStats : null;
    const statusText = getStatusBadgeText(deliverabilityStatus.status);
    const badgeDetailsStatusText = unsubscribeStats
        ? getEmailBadgeDetailsStatusText(
              deliverabilityStatus.status,
              unsubscribeStats.unsubscribeSource,
              unsubscribeStats.userUnsubscribedBy?.name
          )
        : statusText;

    return (
        <StatusBadge
            customIcon={isSmsStatus ? ChatRegularOptionsIcon : EnvelopeNewIcon}
            level={getStatusBadgeLevel(deliverabilityStatus.status)}
            text={iconOnly ? '' : statusText}
            detail={getStatusBadgeDetail(
                deliverabilityStatus.status,
                badgeDetailsStatusText,
                isSmsStatus
                    ? deliverabilityStatus.optOutTime
                    : deliverabilityStatus.unsubscribeStats?.unsubscribeTime
            )}
            size='small'
        />
    );
}

function getStatusBadgeLevel(status: EmailStatus | SmsStatus): Level {
    switch (status) {
        case 'GLOBALLY_UNSUBSCRIBED':
        case 'OPTED_OUT':
            return 'warning';
        case 'BOUNCING':
        case 'UNDELIVERABLE':
        default:
            return 'alert';
    }
}

function getStatusBadgeText(status: EmailStatus | SmsStatus): string {
    switch (status) {
        case 'GLOBALLY_UNSUBSCRIBED':
            return 'Unsubscribed';
        case 'OPTED_OUT':
            return 'Opted-out';
        case 'UNDELIVERABLE':
            return 'Undeliverable';
        case 'BOUNCING':
        default:
            return 'Bouncing';
    }
}

function getEmailBadgeDetailsStatusText(
    status: EmailStatus | SmsStatus,
    source: UnsubscribeSource,
    unsubscribedUser: string | null | undefined
): string {
    if (status !== 'GLOBALLY_UNSUBSCRIBED') {
        return getStatusBadgeText(status);
    }

    switch (source) {
        case 'NUTSHELL_USER':
            return unsubscribedUser
                ? `Unsubscribed by ${unsubscribedUser}`
                : 'Unsubscribed by a Nutshell user';
        case 'IMPORT':
            return 'Unsubscribed via import';
        case 'RECIPIENT_LINK':
        case 'LIST_UNSUBSCRIBE':
            return 'Unsubscribed manually';
        default:
            return 'Unsubscribed';
    }
}

function getStatusBadgeDetail(
    status: EmailStatus | SmsStatus,
    statusText: string,
    timestamp: string | null | undefined
): React.ReactNode | null | undefined {
    let detailContent = null;
    switch (status) {
        case 'GLOBALLY_UNSUBSCRIBED':
        case 'OPTED_OUT':
            detailContent = (
                <>
                    {statusText} on&nbsp;
                    <FormattedTimestamp
                        isInline={true}
                        datetime={Number(timestamp)}
                        format={FormattedTimestamp.formats.longMonthDayWithYear}
                    />
                </>
            );
            break;
        case 'UNDELIVERABLE':
            detailContent = (
                <>
                    Your SMS messages to this number are
                    <br />
                    not being delivered
                </>
            );
            break;
        case 'BOUNCING':
            detailContent = 'Your emails to this address are bouncing';
            break;
    }

    return <div className='pad-8 text-align-center'>{detailContent}</div>;
}
